import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useContext
} from 'react';
import { AtresWidgetController, usePrevious } from '@atresmedia/a3utils';
import waitForLoad from '@atresmedia/a3utils/dist/utils/waitForLoad';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import useLiveradio from '../../../hooks/useLiveradio';
import useConstructor from '../../../hooks/useConstructor';
import { MainPlayerContext } from '../../../context/mainPlayer';
import { childrenPropType } from '../../../utils';
// import uuid from 'react-uuid';

export const MainController = ({
  renderWidget,
  children,
  onNavigate,
  isStorybook
}) => {
  const main = useRef(document.querySelector('main'));
  // widgets que hay actualmente en el DOM
  const [widgets, setWidgets] = useState({});
  // Guardamos la instancia del controller que detecta los widgets
  const controller = useRef();

  const mainPlayerContext = useContext(MainPlayerContext);

  // Info del directo
  const { stationId } = mainPlayerContext.state.currentStation;

  const [live, loadingLive] = useLiveradio(stationId);
  const prevLive = usePrevious(live);
  // const prevStationId = usePrevious(stationId);

  const getLiveExtraParams = useCallback(
    modelInfo => {
      // Si es la carga inicial o no estamos en directo
      if (!mainPlayerContext.state.data || !mainPlayerContext.state.live) {
        return { playondraw: true, autoplay: true };
      }

      // Si el usuario ha cambiado de emisora reproducimos
      // if (stationId !== prevStationId) {
      //   return { playondraw: true, autoplay: true };
      // }

      const programStillExists = live.current.some(
        e => e.programId === mainPlayerContext.state.activeLive
      );

      const { freewheel } = modelInfo;

      if (freewheel) {
        freewheel.prerolls = false;
      }

      const prevProgram =
        prevLive &&
        prevLive.current.find(
          e => e.programId === mainPlayerContext.state.activeLive
        );

      // Directo normal (los eventos tienen id creado en front que empieza por event-...)
      if (!prevProgram || !prevProgram.isEvent) {
        const result = {
          playondraw: true,
          autoplay: true,
          freewheel
        };
        if (!mainPlayerContext.state.playing) {
          // result.freewheel = undefined;
          mainPlayerContext.dispatch({
            type: 'setDestroyed',
            payload: true
          });
        }
        return result;
      }

      // Si se ha terminado el evento
      if (!programStillExists) {
        // Tenemos que parar el player
        mainPlayerContext.dispatch({
          type: 'set',
          payload: { ...mainPlayerContext, destroyed: true, playing: false }
        });

        // Le ponemos un timeout para que no intente pausar el player mientras se esta desmontando por el destroyed=true
        // setTimeout(() => {
        //   mainPlayerContext.dispatch({
        //     type: 'setPlaying',
        //     payload: false
        //   });
        // }, 300);
      }

      return { playondraw: true, autoplay: true, freewheel };
    },
    [mainPlayerContext, live, prevLive]
  );

  const updateHeaderEventClass = useCallback((isEvent = false) => {
    const header = document.getElementById('header');
    if (header) {
      if (isEvent) {
        header.classList.add('header-event');
      } else {
        header.classList.remove('header-event');
      }
    }
  }, []);

  // Actualizamos info del directo
  useEffect(() => {
    if (live && live.current && live.current.length > 0 && prevLive !== live) {
      let hasEvent = false;
      const newLive = {
        ...live,
        current: live.current.map(model => {
          const isEvent = model.type.toUpperCase().includes('EVENT');
          if (isEvent) {
            hasEvent = true;
          }
          const currModel = {
            ...model,
            isEvent,
            programId:
              model.programId ||
              (model.freewheel &&
                (model.freewheel.id || `${model.title}-${model.startDate}`)) // Creamos id porque los eventos no tienen
          };
          return {
            ...currModel,
            // programId: model.programId ? model.programId : `eventId-${uuid()}`,
            tipo: 'audio',
            type: 'audio',
            sources: [
              {
                src: `${currModel.html5Url}?random=${Math.random()}`,
                type:
                  currModel.html5Url && currModel.html5Url.includes('.m3u8')
                    ? 'application/vnd.apple.mpegurl'
                    : 'audio/mp4'
              }
            ], // Metemos esta condicion para evitar que en el cambio de programa haga autoplay
            live: true,
            audioType: 'LIVE',
            ...getLiveExtraParams(currModel)
          };
        })
      };

      updateHeaderEventClass(hasEvent);

      // Controlar que si un evento ha terminado pausar el player de directo

      // Actualizamos el activeLive para asegurarnos que el que estaba seleccionado sigue en emision
      const activeLive = newLive.current.some(
        e => e.programId === mainPlayerContext.state.activeLive
      )
        ? mainPlayerContext.state.activeLive
        : newLive.current[0].programId;

      mainPlayerContext.dispatch({
        type: 'set',
        payload: {
          ...mainPlayerContext.state,
          data: newLive,
          activeLive,
          loadingLive
        }
      });
    }
  }, [
    live,
    prevLive,
    loadingLive,
    mainPlayerContext,
    getLiveExtraParams,
    updateHeaderEventClass
  ]);

  const isInternal = useCallback(url => {
    // Ponemos origin apuntando a pre en local para poder apuntar a paginas con contenido
    // const origin =
    //   process.env.NODE_ENV === 'development'
    //     ? 'http://ondacero.antena3tv.es'
    //     : window.location.origin;
    const { origin } = window.location;

    const internal = url.includes(
      origin + process.env.REACT_APP_ROUTER_BASENAME
    );

    let inPage = false;
    if (internal && url.includes('#')) {
      try {
        inPage = true;
        const id = url.split('#')[1];
        if (id) {
          const elem = document.querySelector(`#${id}`);
          if (elem) {
            return { internal, inPage: true };
          }
        }
      } catch (err) {
        console.log('Error al tratar de navegar a elemento de la pagina');
        return { internal };
      }
    }

    return { internal, inPage };
  }, []);

  // En cada click, si es un enlace con url propia de esta PWA seteamos navigable a true para que se renderice el Router
  const handleWindowClick = useCallback(
    event => {
      const link =
        (event.target.nodeName === 'A' ? event.target : null) ||
        event.target.closest('a');

      if (link) {
        const urlObj = new URL(link.href);
        const sameOrigin =
          urlObj.origin === window.location.origin ||
          process.env.NODE_ENV === 'development';
        const noBlock = link.getAttribute('noBlock');
        const targetBlank = link.getAttribute('target') === '_blank';

        if (noBlock || targetBlank || !sameOrigin) {
          return;
        }
        // Comprobamos si la url es interna
        const { inPage } = isInternal(link.href);

        // Evitamos que se navegue
        if (!inPage) {
          event.preventDefault();
          onNavigate(link.href);
        } else {
          // navegar in page
        }
      }
    },
    [onNavigate, isInternal]
  );

  useEffect(() => {
    window.addEventListener('click', handleWindowClick);

    return () => {
      window.removeEventListener('click', handleWindowClick);
    };
  }, [handleWindowClick]);

  useEffect(() => {
    if (!main.current) {
      main.current = document.querySelector('main');
    }
    if (!controller.current) {
      // Creamos un widget controller que detectara todos los widgets de la pagina tanto ya exitentes como nuevos
      controller.current = new AtresWidgetController(document.body, setWidgets);
    }
  }, [isStorybook]);

  useConstructor(() => {
    firebase.auth().onAuthStateChanged(user => {
      if (user) {
        // https://firebase.google.com/docs/reference/js/firebase.User
        // var uid = user.uid;
        mainPlayerContext.dispatch({ type: 'login' });
      } else {
        // User is signed out
        mainPlayerContext.dispatch({ type: 'logout' });
      }
    });
  });

  /* eslint-disable no-underscore-dangle */
  useConstructor(() => {
    waitForLoad(window, '__tcfapi').then(() => {
      window.__tcfapi('addEventListener', 2, (data, success) => {
        const { tcString } = data;
        if (success && tcString) {
          mainPlayerContext.dispatch({ type: 'consentAnswered' });
        }
      });
    });
  });

  return (
    <React.Fragment>
      {children}
      {Object.values(widgets).map(w => {
        if (renderWidget && w.isConnected) {
          return renderWidget(w);
        }
        return null;
      })}
    </React.Fragment>
  );
};

MainController.propTypes = {
  renderWidget: PropTypes.func,
  children: childrenPropType,
  onNavigate: PropTypes.func.isRequired,
  isStorybook: PropTypes.bool
};

MainController.defaultProps = {
  renderWidget: null,
  children: null,
  isStorybook: false
};

export default withRouter(MainController);
